import { SlotGame } from "@nolimitcity/slot-game/bin/SlotGame";
import { FlowStateController } from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import { gsap } from "gsap";

import { AbstractReel } from "../../../abstracts/elements/AbstractReel";
import { EventsManager, ReelsEvents, WinEvents } from "../../../abstracts/events/EventsManager";
import { AbstractFlowPresentation } from "../../../abstracts/flow/AbstractFlowStatePresentation";
import { AbstractGameMode } from "../../../abstracts/flow/AbstractGameMode";
import { TimelineLabels } from "../../../abstracts/types/enums";
import { DDD } from "../../DDD";
import { winLevels } from "../../config/DDDConfig";
import DDDReel from "../../modules/Reels/DDDReel";
import { DDDXNudgeSymbol } from "../../modules/Reels/symbols/DDDXNudgeSymbol";
import { DDDWinView } from "../../modules/Winnings/DDDWinView";
import { totalWinHandler } from "../decorators/DDDTotalWinHandler";

export class DDDNormalWinPresentation extends AbstractFlowPresentation {
    protected DDDReels: DDDReel[];
    protected winView: DDDWinView;

    constructor(name: string = `DDDNormalWinPresentation`) {
        super(name);
    }

    protected handleEvents(): void {
        super.handleEvents();

        EventsManager.on(WinEvents.GET_WINNINGS_VIEW, (winView: DDDWinView) => {
            this.winView = winView;
        });

        EventsManager.on(ReelsEvents.GET_REELS, (reels: AbstractReel[]) => {
            this.DDDReels = reels as DDDReel[];
        });
    }

    public start(flowStateController: FlowStateController) {
        super.start(flowStateController);

        if (this.model.gameData.accumulatedRoundWin <= 0 && !DDD.model.xNudgeMap.includes(true)) {
            if (
                (this.model.gameData.mode === AbstractGameMode.FREESPIN ||
                    this.model.gameData.mode === AbstractGameMode.SUPER_FREESPIN) &&
                this.model.gameData.nextMode === AbstractGameMode.NORMAL
            ) {
                this.addSpecialAnimations();
            } else {
                this.finishPresentation();
            }
        } else {
            this.handleWinnings();
        }
    }

    protected handleWinnings() {
        const isWinBelowStake: boolean = DDD.model.isWinBelowStake();
        const isTotalWin =
            (this.model.gameData.mode !== AbstractGameMode.NORMAL &&
                this.model.gameData.nextMode === AbstractGameMode.NORMAL) ||
            this.model.gameData.mode === AbstractGameMode.FREESPIN ||
            this.model.gameData.mode === AbstractGameMode.SUPER_FREESPIN ||
            this.model.gameData.mode === AbstractGameMode.RESPIN;

        if (isWinBelowStake) {
            SlotGame.keypadPlugin.setWin(
                this.model.gameData.accumulatedRoundWin,
                isTotalWin,
                isWinBelowStake,
                false
            );
        }

        totalWinHandler({
            winLevels: winLevels,
            timeline: this.flowPresentationTimeline,
            winScreen: this.winView.totalWinScreen,
            flowStateController: this.controller,
            winIsBelowStake: isWinBelowStake,
            reels: this.DDDReels,
            keypadPlugin: this.keypad,
        });

        this.xNudgeDeadTease();

        this.flowPresentationTimeline.call(
            () => {
                if (this.model.gameData.brokeTheBank) {
                    SlotGame.keypadPlugin.setWin(-1, false, true, true);
                } else {
                    SlotGame.keypadPlugin.setWin(
                        this.model.gameData.accumulatedRoundWin,
                        isTotalWin,
                        isWinBelowStake,
                        false
                    );
                }
            },
            [],
            TimelineLabels.TEXT_COUNT_COMPLETE
        );

        this.flowPresentationTimeline.resume();
    }

    protected xNudgeDeadTease(): void {
        const isWin = DDD.model.gameData.win > 0;
        const delayBeforeNudgeAnimations = isWin && DDD.model.xNudgeMap.includes(true) ? 0.5 : 0;

        this.flowPresentationTimeline.add(gsap.to({}, { duration: delayBeforeNudgeAnimations }));

        this.DDDReels.forEach((reel, reelIndex: number) => {
            if (this.model.gameData) {
                reel.getAllVisibleStopSymbols().find((sym) => {
                    if (sym.visible && sym.name.includes(`XN`)) {
                        this.flowPresentationTimeline.add(
                            (sym as DDDXNudgeSymbol).xNudgeAllAnimationsFlow(
                                this.model.gameData.xNudgeMultipliersNext[reelIndex],
                                reelIndex
                            )
                        );
                    }
                });
            }
        });
    }

    protected finishPresentation() {
        const delay =
            this.model.gameData.nextMode !== AbstractGameMode.NORMAL &&
            this.model.gameData.nextMode !== this.model.gameData.mode
                ? 0.4
                : 0;
        gsap.delayedCall(delay, () => {
            super.finishPresentation();
        });
    }
}
